import { useCallback, useEffect, useState, useContext } from 'react';
import { deleteSandbox, downloadSandboxToJson, getAllSandboxesExtended, toggleSandboxShared } from '../../../Services/BorgAPIClient';
import moment from 'moment';
import ChangeConfirmationDialog from '../../../Components/ChangeConfirmationDialog';
import {
  Table, TableBody, TableCell, TableContainer, TableRow, Paper, Button, Box,
  Typography,
  AlertColor,
  Tooltip,
  Switch,
  IconButton,
  Badge,
  Checkbox,
  Menu,
  MenuItem,
  Divider
} from '@mui/material';
import { LoggedInUserContext } from '../../../Components/Admin/AdminPageContainer';
import { Roles } from 'src/Constants/BorgEnums';
import { SandboxComposite } from 'src/Constants/Types';
import { SortableTableHead } from 'src/Components/Admin/TableComponents/SortableTableHead';
import { compareSandboxes } from './SandboxComparer';
import { useTableSort } from 'src/Hooks/useTableSort';
import { useTableFilter } from 'src/Hooks/useTableFilter';
import { SandboxFiltersPopover } from 'src/Components/SandboxFilterPopover';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useMultiSelect } from 'src/Hooks/useMultiSelect';
import { MoreHoriz } from '@mui/icons-material';

export type SandboxFilters = {
  organizationIds?: string[];
  isShared?: boolean[];
  ownerStatuses?: string[];
};

export const determineOwnerStatus = (sandbox: SandboxComposite) => {
  if (sandbox.userId === '00000000-0000-0000-0000-000000000000') {
    return 'Deleted';
  } else if (sandbox.userEnabled) {
    return 'Enabled';
  } else {
    return 'Disabled';
  }
};

type Props = { onNotification: (message: string, notificationType?: AlertColor) => void };

const SandboxesAdminPage = (props: Props) => {

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [bulkActionsAnchor, setBulkActionsAnchor] = useState<HTMLElement | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [menuSandbox, setMenuSandbox] = useState<SandboxComposite | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [sandboxList, setSandboxList] = useState<SandboxComposite[]>([]);
  const [sandboxToChange, setSandboxToChange] = useState<SandboxComposite | null>(null);
  const [sandboxToDelete, setSandboxToDelete] = useState<SandboxComposite | null>(null);

  const {
    filters,
    setFilters,
    filterAnchor,
    openFilters,
    closeFilters,
    isFiltersOpen,
    filteredItems: filteredSandboxes,
  } = useTableFilter({
    items: sandboxList,
    initialFilters: {} as SandboxFilters,
    predicate: (sandbox, criteria) => {
      if (criteria.organizationIds?.length && !sandbox.organizationId) return false
      if (criteria.organizationIds?.length && sandbox.organizationId && !criteria.organizationIds.includes(sandbox.organizationId)) return false;
      if (criteria.isShared?.length && !criteria.isShared.includes(sandbox.isShared)) return false;
      if (criteria.ownerStatuses?.length && sandbox.userEnabled !== undefined && !criteria.ownerStatuses.includes(determineOwnerStatus(sandbox))) return false;
      return true;
    },
  });

  const {
    sortedItems: sortedSandboxes,
    sortBy,
    sortDirection,
    handleSort,
  } = useTableSort<SandboxComposite, keyof SandboxComposite>(
    filteredSandboxes,
    'title',
    compareSandboxes
  );

  const multiSelect = useMultiSelect(sortedSandboxes, (s) => s.id);

  const loggedInUser = useContext(LoggedInUserContext)?.loggedInUser;

  const fetchSandboxes = useCallback(async () => {
    if (loggedInUser) {
      try {
        const result = await getAllSandboxesExtended();
        setSandboxList(result.data as SandboxComposite[]);
        setIsLoaded(true);
      } catch (error) {
        console.error('Failed to fetch sandboxes:', error);
        props.onNotification("Failed to fetch sandboxes", "error");
      }
    }
  }, [loggedInUser]);

  useEffect(() => {
    fetchSandboxes();
  }, [fetchSandboxes]);

  const closeConfirmDeleteDialog = () => {
    setOpenDialog(false);
  };

  const handleDeleteClick = (sandbox: SandboxComposite) => {
    setSandboxToDelete(sandbox);
    setOpenDialog(true);
  };

  const handleConfirmDelete = async () => {
    if (sandboxToDelete) {
      try {
        await deleteSandbox(sandboxToDelete.id);
      }
      catch (error) {
        props.onNotification("Failed to delete sandbox", "error");
      }
      fetchSandboxes();
    }
    setOpenDialog(false);
    setSandboxToDelete(null);
  };

  const handleDownloadClick = async (sandbox: SandboxComposite) => {
    try {
      await downloadSandboxToJson(sandbox.id, `${sandbox.title} ${sandbox.id}.json`);
    }
    catch (error) {
      console.error("Failed to download sandbox");
      props.onNotification("Failed to download sandbox", "error");
    }
  };

  const handleToggleShared = async (sandbox: SandboxComposite) => {
    setSandboxToChange(sandbox);  //(used to update stats panel) set user being updated 

    try {
      await toggleSandboxShared(sandbox.id);
      fetchSandboxes(); // Update sandbox list after successful API call
    } catch (error) {
      const errorMessage = (error as { title?: string }).title || 'An unknown error occurred';
      console.error(errorMessage);
      props.onNotification(errorMessage, "error");
    }
    setSandboxToChange(null); //(used to update stats panel) Clear out user set 
  };

  const isSysAdminRowRestricted = () =>
    loggedInUser?.roles[0] !== Roles.SystemAdmin;

  const handleBulkActionsMenuClose = () => {
    setBulkActionsAnchor(null);
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    sandbox: SandboxComposite
  ) => {
    setAnchorEl(event.currentTarget);
    setMenuSandbox(sandbox);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuSandbox(null);
  };

  const handleBulkSandboxDownload = () => {
    multiSelect.selectedIds.forEach(async (sandboxId) => {
      const sandbox = sandboxList.find(s => s.id === sandboxId);
      if (sandbox) {
        await handleDownloadClick(sandbox);
      }
    });
    handleBulkActionsMenuClose();
  };

  const handleBulkSandboxDeleteClick = () => {
    setOpenDialog(true);
    setSandboxToDelete(null);
    handleBulkActionsMenuClose();
  }

  const handleBulkSandboxDelete = async () => {
    try {
      await Promise.all(
        multiSelect.selectedIds.map(async (sandboxId) => {
          const sandbox = sandboxList.find((s) => s.id === sandboxId);

          if (sandbox) {
            await deleteSandbox(sandbox.id);
          }
        })
      );

      await fetchSandboxes();
    } finally {
      setOpenDialog(false);
      setSandboxToDelete(null);
    }
  };

  const isMenuOpenForSandbox = (sandbox: SandboxComposite) => Boolean(anchorEl) && menuSandbox?.id === sandbox.id;

  const renderSandboxRow = (sandbox: SandboxComposite, index: number) => {
    const timeLastActive = sandbox.userLastActive ? moment(sandbox.userLastActive).fromNow() : null;
    const timeLastAccessed = moment(sandbox.lastAccessed);
    const accessCount = sandbox.accessCount ?? 0;

    const timeCreated = moment(sandbox.timeCreated);
    const timeLastModified = moment(sandbox.lastModified);

    const createdOrUpdated = timeCreated === timeLastModified ? `Created ${timeCreated.fromNow()}` : `Modified ${timeLastModified.fromNow()}`

    return (
      <TableRow
        key={index}
        style={{
          backgroundColor: sandbox.userEnabled ? '' : '#8e5d64',
        }}
      >
        <TableCell padding="checkbox">
          <Checkbox
            checked={multiSelect.isSelected(sandbox.id)}
            onClick={(event) => {
              multiSelect.handleRowSelection(event, sandbox.id, index);
            }}
          />
        </TableCell>
        <TableCell>
          <Box>{sandbox.title}</Box>
        </TableCell>
        <TableCell>
          <Switch
            checked={sandbox.isShared}
            color="success"
            onChange={() => handleToggleShared(sandbox)}
            size="small"
            inputProps={{ "aria-label": sandbox.isShared ? "Enabled" : "Disabled" }}
            disabled={isSysAdminRowRestricted()}
          />
        </TableCell>
        <TableCell>
          <Box>{sandbox.userName}</Box>
        </TableCell>
        <TableCell>
          <Box>{sandbox.organization}</Box>
        </TableCell>
        <TableCell>{determineOwnerStatus(sandbox)}</TableCell>
        <TableCell>
          {timeLastActive}
        </TableCell>
        <TableCell>
          {timeLastAccessed.fromNow()}
        </TableCell>
        <TableCell>
          {accessCount}
        </TableCell>
        <TableCell>
          {createdOrUpdated}
        </TableCell>
        <TableCell>
          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <IconButton
              size="small"
              onClick={(e) => handleMenuOpen(e, sandbox)}
              color={isMenuOpenForSandbox(sandbox) ? "secondary" : "default"}
            >
              <MoreHoriz fontSize="small" />
            </IconButton>
          </Box>
        </TableCell>
      </TableRow>
    );
  }

  const SANDBOX_COLUMNS_CONFIG = [
    { id: 'title', label: 'Sandbox Name', width: '10%', align: 'left' },
    { id: 'isShared', label: 'Shared Status', width: '5%' },
    { id: 'userName', label: 'Owner', width: '10%', align: 'left' },
    { id: 'organization', label: 'Organization', width: '10%', align: 'left' },
    { id: 'userEnabled', label: 'Owner Status', width: '10%', align: 'left' },
    { id: 'userLastActive', label: 'Owner Last Active', width: '10%', align: 'left' },
    { id: 'lastAccessed', label: 'Last Accessed', width: '10%', align: 'left' },
    { id: 'accessCount', label: 'Use Count', width: '10%', align: 'left' },
    { id: 'lastModified', label: 'Created/Modified', width: '10%', align: 'right' },
  ] as const;

  if (isLoaded && sandboxList.length > 0) {
    return (
      <Box sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <div style={{ margin: '16px 16px 16px auto' }}>
          <Badge badgeContent={0} color="secondary">
            <IconButton onClick={openFilters} aria-label="filter users">
              <FilterListIcon />
            </IconButton>
          </Badge>
        </div>
        <Box sx={{
          flex: 1,
          overflow: 'hidden',
          borderRadius: 1,
          marginY: 2,
          bgcolor: 'primary.dark',
          display: 'flex',
          flexDirection: 'column'
        }}>
          <TableContainer component={Paper} sx={{ flex: 1, overflow: 'auto' }}>
            <Table stickyHeader>
              <SortableTableHead
                columns={SANDBOX_COLUMNS_CONFIG}
                sortBy={sortBy}
                sortDirection={sortDirection}
                onSort={handleSort}
                prependCell={
                  <TableCell sx={{ width: '5%' }} padding='checkbox'>
                    <Checkbox
                      checked={sortedSandboxes.length > 0 && sortedSandboxes.every(s => multiSelect.selectedIds.includes(s.id))}
                      onChange={(e) => multiSelect.toggleAll(e.target.checked)}
                    />
                  </TableCell>
                }
                appendCell={
                  <>
                    {multiSelect.selectedIds.length === 0 ? (
                      <TableCell sx={{ width: '5%' }} align="center">Actions</TableCell>
                    ) : (
                      <TableCell sx={{ width: '5%' }} align="center">
                        <Tooltip title="Bulk actions">
                          <IconButton
                            onClick={(e) => setBulkActionsAnchor(e.currentTarget)}
                            size="small"
                            sx={{
                              color: Boolean(bulkActionsAnchor) ? 'secondary.main' : 'white'
                            }}
                          >
                            <MoreHoriz />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    )}
                  </>
                }
              />
              <TableBody id="list-sandboxes">
                {sortedSandboxes.map((sandbox, index) => renderSandboxRow(sandbox, index))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        <SandboxFiltersPopover
          anchorEl={filterAnchor}
          open={isFiltersOpen}
          filters={filters}
          onChange={setFilters}
          onClose={closeFilters}
          sandboxes={sandboxList}
          organizations={Array.from(
            new Map(
              sandboxList
                .filter(s => s.organizationId && s.organization)
                .map(s => [s.organizationId!, { id: s.organizationId!, name: s.organization! }])
            ).values()
          )} />

        <ChangeConfirmationDialog
          title="Confirm Delete"
          message={
            `This action cannot be undone.  Are you sure 
            you want to delete "${sandboxToDelete?.title}" for all users?`}
          open={openDialog && !!sandboxToDelete}
          onClose={closeConfirmDeleteDialog}
          onCancel={closeConfirmDeleteDialog}
          onConfirm={handleConfirmDelete}
        />

        <ChangeConfirmationDialog
          title="Confirm Bulk Delete"
          message={`This action cannot be undone. Are you sure you want to delete ${multiSelect.selectedIds.length} sandbox${multiSelect.selectedIds.length !== 1 ? 'es' : ''}?`}
          open={openDialog && !sandboxToDelete}
          onClose={closeConfirmDeleteDialog}
          onCancel={closeConfirmDeleteDialog}
          onConfirm={handleBulkSandboxDelete}
        />

        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem
            onClick={() => {
              handleDownloadClick(menuSandbox!);
              handleMenuClose();
            }}
          >
            Download
          </MenuItem>

          <MenuItem
            onClick={() => {
              handleDeleteClick(menuSandbox!);
              handleMenuClose();
            }}
            sx={{ color: "error.main" }}
          >
            Delete
          </MenuItem>
        </Menu>

        <Menu
          anchorEl={bulkActionsAnchor}
          open={Boolean(bulkActionsAnchor)}
          onClose={handleBulkActionsMenuClose}
        >
          <MenuItem disabled sx={{ fontWeight: 'bold' }}>
            {multiSelect.selectedIds.length} sandbox{multiSelect.selectedIds.length !== 1 ? 's' : ''} selected
          </MenuItem>
          <Divider />
          <MenuItem onClick={handleBulkSandboxDownload}>
            Download sandboxes
          </MenuItem>
          <MenuItem
            onClick={handleBulkSandboxDeleteClick}
            sx={{ color: "error.main" }}
          >
            Delete sandboxes
          </MenuItem>
        </Menu>
      </Box >
    );
  } else if (isLoaded) {
    return (
      <Box sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column'
      }}>
        <Typography align='center' variant='h2' sx={{ margin: 10 }}>No Sandboxes Found</Typography>
      </Box>
    );
  } else {
    return <div>Loading...</div>;
  }
}

export default SandboxesAdminPage;